import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Outlet, Link, useLocation } from 'react-router-dom';
import logo from '../images/logo.png';
import './AppLayout.css'; // Import specific styles
import api from '../services/api';
import UserMenu from '../components/UserMenu';
import TrialBanner from '../components/TrialBanner';
import TrialPopup from '../components/TrialPopup';

const AppLayout: React.FC = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [user, setUser] = useState<{ first_name: string; last_name: string; package?: { has_chat?: boolean; is_test?: boolean }; is_expired?: boolean; expire_at?: string | null } | null>(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    useEffect(() => {
        const fetchUser = async () => {
            const token = localStorage.getItem('token');
            if (token) {
                try {
                    const res = await api.get('/user/profile/me');
                    setUser(res.data);
                } catch (err) {
                    console.error('Failed to fetch user', err);
                    localStorage.removeItem('token'); // Invalid token
                }
            }
        };
        fetchUser();
    }, []);

    const changeLanguage = (lng: string) => {
        // Get current path segments
        const currentPath = location.pathname;
        const pathSegments = currentPath.split('/').filter(Boolean);

        // If the first segment is a language code, replace it
        if (['fr', 'ar'].includes(pathSegments[0])) {
            pathSegments[0] = lng;
        } else {
            // Should not happen with new routing, but safe fallback
            pathSegments.unshift(lng);
        }

        // Preserve query string parameters
        const newPath = `/${pathSegments.join('/')}${location.search}`;
        navigate(newPath);
        setMobileMenuOpen(false);
    };

    const handleLogout = () => {
        // Notify the API to free the session slot
        const currentToken = localStorage.getItem('token');
        if (currentToken) {
            const backendUrl = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '') || '';
            fetch(`${backendUrl}/auth/logout`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${currentToken}` },
                keepalive: true
            }).catch(() => {});
        }
        localStorage.removeItem('token');
        setUser(null);

        navigate(`/${i18n.language}`);
        setMobileMenuOpen(false);
    };

    const toggleMobileMenu = () => {
        setMobileMenuOpen(!mobileMenuOpen);
    };

    const closeMobileMenu = () => {
        setMobileMenuOpen(false);
    };

    const isTrial = !!user?.package?.is_test && !user?.is_expired;
    const isExpired = !!user?.is_expired;

    return (
        <div className="app-layout">
            <TrialBanner isTrial={isTrial} isExpired={isExpired} expireAt={user?.expire_at} />
            <TrialPopup isTrial={isTrial} isExpired={isExpired} />
            <header className="landing-header">
                <div className="logo-container">
                    <Link to={`/${i18n.language}`}>
                        <img src={logo} alt="Berti-Law Logo" className="landing-logo-img" />
                    </Link>
                </div>

                <button className="mobile-menu-toggle" onClick={toggleMobileMenu}>
                    <i className={`bi ${mobileMenuOpen ? 'bi-x-lg' : 'bi-list'}`}></i>
                </button>

                <div className="desktop-nav-container">
                    <nav className="main-nav desktop-nav">
                        <Link to={`/${i18n.language}`} className="landing-nav-link" onClick={closeMobileMenu}>{t('nav.home')}</Link>
                        {(!user || user?.package) && (
                            <Link to={`/${i18n.language}/search`} className="landing-nav-link" onClick={closeMobileMenu}>{t('nav.search')}</Link>
                        )}
                        {(!user || user?.package?.has_chat !== false) && (
                            <Link to={`/${i18n.language}/chat`} className="landing-nav-link" onClick={closeMobileMenu}>{t('nav.chatbot')}</Link>
                        )}
                        {user ? "" : (
                            <Link to={`/${i18n.language}/login`} className="landing-nav-link" onClick={closeMobileMenu}>{t('nav.account')}</Link>
                        )}
                        <Link to={`/${i18n.language}/help`} className="landing-nav-link" onClick={closeMobileMenu}>{t('nav.help')}</Link>
                    </nav>
                </div>

                <div className={`header-actions ${mobileMenuOpen ? 'mobile-open' : ''}`}>
                    <nav className="main-nav mobile-nav">
                        <Link to={`/${i18n.language}`} className="landing-nav-link" onClick={closeMobileMenu}>{t('nav.home')}</Link>
                        {(!user || user?.package) && (
                            <Link to={`/${i18n.language}/search`} className="landing-nav-link" onClick={closeMobileMenu}>{t('nav.search')}</Link>
                        )}
                        {(!user || user?.package?.has_chat !== false) && (
                            <Link to={`/${i18n.language}/chat`} className="landing-nav-link" onClick={closeMobileMenu}>{t('nav.chatbot')}</Link>
                        )}
                        {user ? "" : (
                            <Link to={`/${i18n.language}/login`} className="landing-nav-link" onClick={closeMobileMenu}>{t('nav.account')}</Link>
                        )}
                        <Link to={`/${i18n.language}/help`} className="landing-nav-link" onClick={closeMobileMenu}>{t('nav.help')}</Link>
                    </nav>

                    <div className="lang-switcher">
                        <button onClick={() => changeLanguage('fr')} className={`lang-btn ${i18n.language === 'fr' ? 'active' : ''}`}>FR</button>
                        <button onClick={() => changeLanguage('ar')} className={`lang-btn ${i18n.language === 'ar' ? 'active' : ''}`}>AR</button>
                    </div>

                    {user ? (
                        <UserMenu user={user} onLogout={handleLogout} variant="light" />
                    ) : (
                        <div className="auth-buttons">
                            <button onClick={() => { navigate(`/${i18n.language}/register`); closeMobileMenu(); }} className="register-btn">
                                {t('common.register')}
                            </button>
                            <button onClick={() => { navigate(`/${i18n.language}/login`); closeMobileMenu(); }} className="login-btn">
                                {t('landing.login')}
                            </button>
                        </div>
                    )}
                </div>
            </header >

            <main className="app-content">
                <Outlet />
            </main>

            <footer className="landing-footer">
                <div className="footer-content">
                    <div className="footer-copyright">
                        Copyright BERTI Editions, {new Date().getFullYear()}
                    </div>
                    <div className="footer-contact">
                        <div className="footer-contact-title">
                            <i className="bi bi-telephone-fill me-2"></i>
                            {t('layout.footer.contactUs', 'Nous contacter')} : BERTI Law
                        </div>
                        <div className="footer-contact-line">
                            <i className="bi bi-geo-alt me-2"></i>
                            Lot. EN NADJAH N° 24, 16047 DELY IBRAHIM, ALGER
                        </div>
                        <div className="footer-contact-line">
                            <i className="bi bi-phone me-2"></i>
                            <a href="tel:+213794020139">(+213) 07 94 02 01 39</a>
                        </div>
                        <div className="footer-contact-line">
                            <i className="bi bi-envelope me-2"></i>
                            <a href="mailto:info@berti-editions.com">info@berti-editions.com</a>
                            <span className="footer-contact-sep"> · </span>
                            <span className="footer-contact-label">{t('layout.footer.commercial', 'Commercial')} :</span>
                            <a href="mailto:bertieditions@outlook.fr"> bertieditions@outlook.fr</a>
                        </div>
                    </div>
                </div>
            </footer>
        </div >
    );
};

export default AppLayout;
